import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme as useCustomTheme } from '../../../providers';
import { useAuth } from '../../../hooks';
import { useNotification } from '../../../hooks';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MenuIcon from '@mui/icons-material/Menu';
import { headerStyles } from './Header.styles';

const Header = ({ onMobileMenuToggle }) => {
  const { t, i18n } = useTranslation();
  const { isDarkMode, toggleTheme } = useCustomTheme();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const getInitials = name => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'tr' : 'en';
    i18n.changeLanguage(newLang);
  };

  const companyName = user?.company?.name || user?.companyName || '';
  const isIstanbulProje = companyName?.toLowerCase().includes('tanbul') && companyName?.toLowerCase().includes('proje');
  const isAiThinks = !isIstanbulProje; // Default to AI Thinks if not Istanbul Proje

  const logoSrc = isIstanbulProje
    ? (isDarkMode ? '/assets/istanbul-proje-dark.png' : '/assets/istanbul-proje-dark.png')
    : (isDarkMode ? '/assets/ai-thinks-logo-light.png' : '/assets/ai-thinks-logo-dark.png');
  
  const brandName = isIstanbulProje ? 'İstanbul Proje' : 'AI Thinks';
  return (
    <Box sx={headerStyles.container}>
      <Box sx={headerStyles.leftSection}>
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMobileMenuToggle}
            sx={headerStyles.hamburgerButton}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box
          sx={{
            ...headerStyles.logoContainer,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
              transition: 'opacity 0.2s ease',
            },
          }}
          onClick={() => navigate('/')}
        >
          <img
            src={logoSrc}
            alt={`${brandName} Logo`}
            style={headerStyles.logoImage(isMobile)}
          />
          <Box sx={headerStyles.brandContainer}>
            <Typography
              variant="h6"
              color="text.primary"
              sx={headerStyles.brandTitle}
            >
              {brandName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={headerStyles.brandSubtitle}
            >
              {t('business.management')}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={headerStyles.rightSection}>
        <Tooltip
          title={i18n.language === 'tr' ? 'Switch to English' : 'Türkçeye geç'}
        >
          <IconButton onClick={toggleLanguage} sx={headerStyles.iconButton}>
            <span style={{ fontSize: 22 }}>
              {i18n.language === 'tr' ? '🇹🇷' : '🇺🇸'}
            </span>
          </IconButton>
        </Tooltip>
        <Tooltip title={isDarkMode ? 'Light Mode' : 'Dark Mode'}>
          <IconButton onClick={toggleTheme} sx={headerStyles.iconButton}>
            {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Tooltip>
        {!isMobile && <Box sx={headerStyles.divider} />}
        <Tooltip title="Profil">
          <IconButton
            sx={headerStyles.profileButton}
            onClick={() => navigate('/profile')}
          >
            <Avatar sx={headerStyles.avatar}>
              {getInitials(user?.name || 'Barış Akın')}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default Header;
